import type { CSSProperties } from "react";
import { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  HelpCircle,
  Loader2,
} from "lucide-react";

// URL del endpoint PHP en SolucionHost que inserta las respuestas en MySQL.
const ENDPOINT_URL = "https://micai.cl/api/guardar.php";

const PURPLE = "#411459";
const GOLD = "#fbbc08";
const PINK = "#e7548f";
const GREEN = "#74b75e";
const ORANGE = "#ff5f00";
const BG = "#FFFDF8";
const INK = "#2A2438";
const MUTED = "#6E6580";
const BORDER = "#E7DCEC";
const CARD = "#FFFFFF";
// Alias para no reescribir cada referencia existente en el archivo:
const PURPLE_SOFT = ORANGE; // acentos secundarios (antes un morado más claro)
const TEAL = GREEN; // éxito / foco (antes un verde azulado distinto)
const ROSE = PINK; // asteriscos obligatorios / errores suaves

// ============================================================================
// ESQUEMA DEL FORMULARIO
// Cada "key" de campo es EXACTAMENTE el título de la pregunta en el Google
// Form real — así el payload que se envía calza por título con el puente de
// Apps Script sin ninguna traducción intermedia. showIf recibe el objeto de
// respuestas completo y decide si el campo se muestra.
//
// LIMITACIÓN CONOCIDA: dos preguntas del formulario real comparten el mismo
// título exacto ("¿Cuentas con alguno de los siguientes diagnósticos?" -
// una para salud mental, otra para discapacidad). El puente de Apps Script
// empareja por título, así que ambas competirían por el mismo título real.
// Aquí las distingo con "tituloReal" para que ambas apunten al título del
// formulario, pero recomiendo renombrar una de las dos en el Google Form
// real (agregar un sufijo distinto) para que el emparejamiento por título
// sea inequívoco del lado de Apps Script.
// ============================================================================
// ============================================================================
// TIPOS DEL ESQUEMA (esto es lo que le faltaba a SECCIONES: al declararlo
// tipado desde el inicio, TypeScript infiere correctamente el tipo de
// "campo"/"sec" en todos los .map/.filter/.forEach de más abajo, sin tener
// que anotar cada callback uno por uno.)
// ============================================================================
type CampoDef = {
  key: string;
  tipo: string;
  requerido?: boolean;
  opciones?: string[];
  otro?: boolean;
  tituloReal?: string;
  escalaLabels?: [string, string];
  dependeDe?: string;
  opcionesPorRegion?: Record<string, string[]>;
  showIf?: (r: Record<string, any>) => boolean;
};
type SeccionDef = {
  titulo: string;
  nota?: string;
  campos: CampoDef[];
};

// Opciones para los desplegables de país, nacionalidad y comuna.
const PAISES: string[] = [
  "Chile",
  "Argentina",
  "Bolivia",
  "Brasil",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Ecuador",
  "El Salvador",
  "Guatemala",
  "Haití",
  "Honduras",
  "México",
  "Nicaragua",
  "Panamá",
  "Paraguay",
  "Perú",
  "República Dominicana",
  "Uruguay",
  "Venezuela",
  "Estados Unidos",
  "Canadá",
  "España",
  "Francia",
  "Italia",
  "Alemania",
  "Reino Unido",
  "Portugal",
  "Países Bajos",
  "Bélgica",
  "Suiza",
  "Austria",
  "Suecia",
  "Noruega",
  "Dinamarca",
  "Finlandia",
  "Irlanda",
  "Polonia",
  "Grecia",
  "Rusia",
  "Ucrania",
  "China",
  "Japón",
  "Corea del Sur",
  "India",
  "Israel",
  "Arabia Saudita",
  "Emiratos Árabes Unidos",
  "Turquía",
  "Tailandia",
  "Vietnam",
  "Filipinas",
  "Indonesia",
  "Malasia",
  "Singapur",
  "Pakistán",
  "Sudáfrica",
  "Nigeria",
  "Egipto",
  "Marruecos",
  "Kenia",
  "Etiopía",
  "Ghana",
  "Argelia",
  "Túnez",
  "Senegal",
  "Australia",
  "Nueva Zelanda",
  "Fiyi",
  "Papúa Nueva Guinea",
];

const NACIONALIDADES: string[] = [
  "Chilena/o",
  "Argentina/o",
  "Boliviana/o",
  "Brasileña/o",
  "Colombiana/o",
  "Costarricense",
  "Cubana/o",
  "Ecuatoriana/o",
  "Salvadoreña/o",
  "Guatemalteca/o",
  "Haitiana/o",
  "Hondureña/o",
  "Mexicana/o",
  "Nicaragüense",
  "Panameña/o",
  "Paraguaya/o",
  "Peruana/o",
  "Dominicana/o",
  "Uruguaya/o",
  "Venezolana/o",
  "Estadounidense",
  "Canadiense",
  "Española/o",
  "Francesa/o",
  "Italiana/o",
  "Alemana/o",
  "Británica/o",
  "Portuguesa/o",
  "Neerlandesa/o",
  "Belga",
  "Suiza/o",
  "Austríaca/o",
  "Sueca/o",
  "Noruega/o",
  "Danesa/o",
  "Finlandesa/o",
  "Irlandesa/o",
  "Polaca/o",
  "Griega/o",
  "Rusa/o",
  "Ucraniana/o",
  "China/o",
  "Japonesa/o",
  "Surcoreana/o",
  "India/o",
  "Israelí",
  "Saudí",
  "Emiratí",
  "Turca/o",
  "Tailandesa/o",
  "Vietnamita",
  "Filipina/o",
  "Indonesia/o",
  "Malasia/o",
  "Singapurense",
  "Pakistaní",
  "Sudafricana/o",
  "Nigeriana/o",
  "Egipcia/o",
  "Marroquí",
  "Keniana/o",
  "Etíope",
  "Ghanesa/o",
  "Argelina/o",
  "Tunecina/o",
  "Senegalesa/o",
  "Australiana/o",
  "Neozelandesa/o",
  "Fiyiana/o",
  "Papú",
];

const COMUNAS_POR_REGION: Record<string, string[]> = {
  "Región de Arica y Parinacota": [
    "Arica",
    "Camarones",
    "General Lagos",
    "Putre",
  ],
  "Región de Tarapacá": [
    "Alto Hospicio",
    "Camiña",
    "Colchane",
    "Huara",
    "Iquique",
    "Pica",
    "Pozo Almonte",
  ],
  "Región de Antofagasta": [
    "Antofagasta",
    "Calama",
    "María Elena",
    "Mejillones",
    "Ollagüe",
    "San Pedro de Atacama",
    "Sierra Gorda",
    "Taltal",
    "Tocopilla",
  ],
  "Región de Atacama": [
    "Alto del Carmen",
    "Caldera",
    "Chañaral",
    "Copiapó",
    "Diego de Almagro",
    "Freirina",
    "Huasco",
    "Tierra Amarilla",
    "Vallenar",
  ],
  "Región de Coquimbo": [
    "Andacollo",
    "Canela",
    "Combarbalá",
    "Coquimbo",
    "Illapel",
    "La Higuera",
    "La Serena",
    "Los Vilos",
    "Monte Patria",
    "Ovalle",
    "Paihuano",
    "Punitaqui",
    "Río Hurtado",
    "Salamanca",
    "Vicuña",
  ],
  "Región de Valparaíso": [
    "Algarrobo",
    "Cabildo",
    "Calera",
    "Calle Larga",
    "Cartagena",
    "Casablanca",
    "Catemu",
    "Concón",
    "El Quisco",
    "El Tabo",
    "Hijuelas",
    "Isla de Pascua",
    "Juan Fernández",
    "La Cruz",
    "La Ligua",
    "Limache",
    "Llaillay",
    "Los Andes",
    "Nogales",
    "Olmué",
    "Panquehue",
    "Papudo",
    "Petorca",
    "Puchuncaví",
    "Putaendo",
    "Quillota",
    "Quilpué",
    "Quintero",
    "Rinconada",
    "San Antonio",
    "San Esteban",
    "San Felipe",
    "Santa María",
    "Santo Domingo",
    "Valparaíso",
    "Villa Alemana",
    "Viña del Mar",
    "Zapallar",
  ],
  "Región Metropolitana de Santiago": [
    "Alhué",
    "Buin",
    "Calera de Tango",
    "Cerrillos",
    "Cerro Navia",
    "Colina",
    "Conchalí",
    "Curacaví",
    "El Bosque",
    "El Monte",
    "Estación Central",
    "Huechuraba",
    "Independencia",
    "Isla de Maipo",
    "La Cisterna",
    "La Florida",
    "La Granja",
    "La Pintana",
    "La Reina",
    "Lampa",
    "Las Condes",
    "Lo Barnechea",
    "Lo Espejo",
    "Lo Prado",
    "Macul",
    "Maipú",
    "María Pinto",
    "Melipilla",
    "Padre Hurtado",
    "Paine",
    "Pedro Aguirre Cerda",
    "Peñaflor",
    "Peñalolén",
    "Pirque",
    "Providencia",
    "Pudahuel",
    "Puente Alto",
    "Quilicura",
    "Quinta Normal",
    "Recoleta",
    "Renca",
    "San Bernardo",
    "San Joaquín",
    "San José de Maipo",
    "San Miguel",
    "San Pedro",
    "San Ramón",
    "Santiago",
    "Talagante",
    "Tiltil",
    "Vitacura",
    "Ñuñoa",
  ],
  "Región del Libertador General Bernardo O'Higgins": [
    "Chimbarongo",
    "Chépica",
    "Codegua",
    "Coinco",
    "Coltauco",
    "Doñihue",
    "Graneros",
    "La Estrella",
    "Las Cabras",
    "Litueche",
    "Lolol",
    "Machalí",
    "Malloa",
    "Marchihue",
    "Mostazal",
    "Nancagua",
    "Navidad",
    "Olivar",
    "Palmilla",
    "Paredones",
    "Peralillo",
    "Peumo",
    "Pichidegua",
    "Pichilemu",
    "Placilla",
    "Pumanque",
    "Quinta de Tilcoco",
    "Rancagua",
    "Rengo",
    "Requínoa",
    "San Fernando",
    "San Vicente",
    "Santa Cruz",
  ],
  "Región del Maule": [
    "Cauquenes",
    "Chanco",
    "Colbún",
    "Constitución",
    "Curepto",
    "Curicó",
    "Empedrado",
    "Hualañé",
    "Licantén",
    "Linares",
    "Longaví",
    "Maule",
    "Molina",
    "Parral",
    "Pelarco",
    "Pelluhue",
    "Pencahue",
    "Rauco",
    "Retiro",
    "Romeral",
    "Río Claro",
    "Sagrada Familia",
    "San Clemente",
    "San Javier",
    "San Rafael",
    "Talca",
    "Teno",
    "Vichuquén",
    "Villa Alegre",
    "Yerbas Buenas",
  ],
  "Región de Ñuble": [
    "Bulnes",
    "Chillán",
    "Chillán Viejo",
    "Cobquecura",
    "Coelemu",
    "Coihueco",
    "El Carmen",
    "Ninhue",
    "Pemuco",
    "Pinto",
    "Portezuelo",
    "Quillón",
    "Quirihue",
    "Ránquil",
    "San Carlos",
    "San Fabián",
    "San Ignacio",
    "San Nicolás",
    "Trehuaco",
    "Yungay",
    "Ñiquén",
  ],
  "Región del Biobío": [
    "Alto Biobío",
    "Antuco",
    "Arauco",
    "Cabrero",
    "Cañete",
    "Chiguayante",
    "Concepción",
    "Contulmo",
    "Coronel",
    "Curanilahue",
    "Florida",
    "Hualpén",
    "Hualqui",
    "Laja",
    "Lebu",
    "Los Álamos",
    "Los Ángeles",
    "Lota",
    "Mulchén",
    "Nacimiento",
    "Negrete",
    "Penco",
    "Quilaco",
    "Quilleco",
    "San Pedro de la Paz",
    "San Rosendo",
    "Santa Bárbara",
    "Santa Juana",
    "Talcahuano",
    "Tirúa",
    "Tomé",
    "Tucapel",
    "Yumbel",
  ],
  "Región de La Araucanía": [
    "Angol",
    "Carahue",
    "Cholchol",
    "Collipulli",
    "Cunco",
    "Curacautín",
    "Curarrehue",
    "Ercilla",
    "Freire",
    "Galvarino",
    "Gorbea",
    "Lautaro",
    "Loncoche",
    "Lonquimay",
    "Los Sauces",
    "Lumaco",
    "Melipeuco",
    "Nueva Imperial",
    "Padre las Casas",
    "Perquenco",
    "Pitrufquén",
    "Pucón",
    "Purén",
    "Renaico",
    "Saavedra",
    "Temuco",
    "Teodoro Schmidt",
    "Toltén",
    "Traiguén",
    "Victoria",
    "Vilcún",
    "Villarrica",
  ],
  "Región de Los Ríos": [
    "Corral",
    "Futrono",
    "La Unión",
    "Lago Ranco",
    "Lanco",
    "Los Lagos",
    "Mariquina",
    "Máfil",
    "Paillaco",
    "Panguipulli",
    "Río Bueno",
    "Valdivia",
  ],
  "Región de Los Lagos": [
    "Ancud",
    "Calbuco",
    "Castro",
    "Chaitén",
    "Chonchi",
    "Cochamó",
    "Curaco de Vélez",
    "Dalcahue",
    "Fresia",
    "Frutillar",
    "Futaleufú",
    "Hualaihué",
    "Llanquihue",
    "Los Muermos",
    "Maullín",
    "Osorno",
    "Palena",
    "Puerto Montt",
    "Puerto Octay",
    "Puerto Varas",
    "Puqueldón",
    "Purranque",
    "Puyehue",
    "Queilén",
    "Quellón",
    "Quemchi",
    "Quinchao",
    "Río Negro",
    "San Juan de la Costa",
    "San Pablo",
  ],
  "Región de Aysén del General Carlos Ibáñez del Campo": [
    "Aysén",
    "Chile Chico",
    "Cisnes",
    "Cochrane",
    "Coyhaique",
    "Guaitecas",
    "Lago Verde",
    "O'Higgins",
    "Río Ibáñez",
    "Tortel",
  ],
  "Región de Magallanes y de la Antártica Chilena": [
    "Antártica",
    "Cabo de Hornos",
    "Laguna Blanca",
    "Porvenir",
    "Primavera",
    "Puerto Natales",
    "Punta Arenas",
    "Río Verde",
    "San Gregorio",
    "Timaukel",
    "Torres del Paine",
  ],
};

const SECCIONES: SeccionDef[] = [
  {
    titulo: "Identificación básica",
    campos: [
      { key: "Teléfono de contacto", tipo: "tel", requerido: true },
      { key: "Año de nacimiento", tipo: "anio", requerido: true },
      {
        key: "¿Con qué género te identificas?",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Femenino",
          "Masculino",
          "No binario",
          "Género fluido",
          "Prefiero no responder",
        ],
        otro: true,
      },
      {
        key: "¿Qué pronombres te gustaría que utilizáramos para dirigirnos a ti durante esta investigación?  ",
        tipo: "multiple",
        opciones: ["Ella", "Él", "Elle", "No utilizo ningún pronombre"],
      },
      {
        key: "Orientación sexual",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Heterosexual",
          "Homosexual",
          "Bisexual",
          "Pansexual",
          "Asexual",
          "Prefiero no responder",
        ],
        otro: true,
      },
      {
        key: "Nacionalidad",
        tipo: "select",
        requerido: true,
        opciones: NACIONALIDADES,
      },
      {
        key: "País de nacimiento",
        tipo: "select",
        requerido: true,
        opciones: PAISES,
      },
    ],
  },
  {
    titulo: "Situación socioeconómica",
    campos: [
      {
        key: "Nivel educativo actual",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Educación básica incompleta",
          "Educación básica completa",
          "Educación media incompleta",
          "Educación media completa",
          "Técnico superior incompleta",
          "Técnico superior en curso",
          "Técnico superior completa",
          "Universitaria incompleta",
          "Universitaria en curso",
          "Universitaria completa",
          "Magíster incompleto",
          "Magíster en curso",
          "Magíster completo",
          "Doctorado incompleto",
          "Doctorado en curso",
          "Doctorado completo",
        ],
      },
      {
        key: "¿Cuál es tu situación ocupacional actual? (durante el último mes)",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Estudiante",
          "Trabajador/a independiente",
          "Trabajador/a dependiente",
          "Cuidador/a",
          "Jubilado/a",
          "Cesante",
        ],
        otro: true,
      },
      {
        key: "Ingresos mensuales promedio personales ",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sin ingresos",
          "Menos de $500.000",
          "Entre $500.001 - $1.000.000",
          "Entre $1.000.001 - $1.500.000",
          "Entre $1.500.001 - $2.000.000",
          "Más de $2.000.000",
          "Prefiero no responder",
        ],
      },
      {
        key: "Tipo de contrato",
        tipo: "unica",
        requerido: true,
        opciones: ["Indefinido", "Plazo fijo", "Honorarios", "Sin contrato"],
        otro: true,
      },
      {
        key: "¿Has tenido cambios significativos de área/rubro laboral o académico, o periodos de discontinuidad (pausas, cesantía prolongada, reingresos) a lo largo de tu vida?",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No"],
      },
    ],
  },
  {
    titulo: "Territorio",
    campos: [
      {
        key: "Región de residencia",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Región de Arica y Parinacota",
          "Región de Tarapacá",
          "Región de Antofagasta",
          "Región de Atacama",
          "Región de Coquimbo",
          "Región de Valparaíso",
          "Región Metropolitana de Santiago",
          "Región del Libertador General Bernardo O'Higgins",
          "Región del Maule",
          "Región de Ñuble",
          "Región del Biobío",
          "Región de La Araucanía",
          "Región de Los Ríos",
          "Región de Los Lagos",
          "Región de Aysén del General Carlos Ibáñez del Campo",
          "Región de Magallanes y de la Antártica Chilena",
        ],
      },
      {
        key: "Comuna de residencia",
        tipo: "select",
        requerido: true,
        dependeDe: "Región de residencia",
        opcionesPorRegion: COMUNAS_POR_REGION,
      },
      {
        key: "Zona de residencia ",
        tipo: "unica",
        requerido: true,
        opciones: ["Urbana", "Rural"],
      },
    ],
  },
  {
    titulo: "Familia y cuidados",
    campos: [
      {
        key: "Estado civil",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Soltero/a",
          "Casado/a",
          "Conviviente",
          "Separado/a",
          "Divorciado/a",
          "Viudo/a",
        ],
      },
      {
        key: "¿Eres madre o padre?",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No"],
      },
      {
        key: "¿Cuántas personas tiene a su cargo?",
        tipo: "unica",
        requerido: true,
        opciones: ["0", "1", "2", "3", "4", "5 o más"],
      },
    ],
  },
  {
    titulo: "Migración y etnicidad",
    campos: [
      {
        key: "¿Has tenido alguna experiencia migratoria?",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, migración internacional",
          "Sí, migración dentro del país (entre regiones o localidades)",
          "Sí, migración internacional y dentro de Chile",
          "No he migrado",
        ],
      },
      {
        key: "En caso que su respuesta anterior fue sí, ¿Cuál fue el principal motivo de tu migración a Chile?  ",
        tipo: "unica",
        opciones: [
          "Estudios",
          "Trabajo",
          "Protección internacional o refugio",
          "Razones personales o familiares",
          "Prefiero no responder",
        ],
        otro: true,
        showIf: (r: any) =>
          r["¿Has tenido alguna experiencia migratoria?"] &&
          r["¿Has tenido alguna experiencia migratoria?"] !== "No he migrado",
      },
      {
        key: "¿Perteneces a algún pueblos indígenas u originario?",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No"],
      },
      {
        key: "Si tu respuesta anterior fue sí, ¿Cuál?",
        tipo: "texto",
        showIf: (r: any) =>
          r["¿Perteneces a algún pueblos indígenas u originario?"] === "Sí",
      },
      {
        key: "  ¿Te consideras afrodescendiente?  ",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No"],
      },
    ],
  },
  {
    titulo: "Neurodivergencia",
    nota: "La neurodivergencia hace referencia a variaciones en el funcionamiento neurológico y cognitivo (como TDAH, autismo, dislexia, entre otras), entendidas como parte de la diversidad natural en las formas de pensar y procesar información, no como déficits.\n\nEn personas con altas capacidades, puede coexistir con estas dando lugar a la doble excepcionalidad (2e), donde las fortalezas cognitivas y los desafíos del neurodesarrollo suelen enmascararse mutuamente, dificultando su identificación. Por eso, muchas personas 2e llegan a la adultez sin diagnóstico formal, o con diagnósticos tardíos.\n\nEsta pregunta busca saber si cuentas con un diagnóstico (formal o autoidentificado) de alguna condición de neurodivergencia.",
    campos: [
      {
        key: "¿Actualmente cuentas con algún diagnóstico por neurodivergencia?",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, cuento con uno o más diagnósticos formales.",
          "Estoy actualmente en proceso de evaluación diagnóstica.",
          "No tengo diagnóstico formal, pero sospecho que podría presentar una/s condición/es.",
          "No, no cuento con diagnósticos ni sospechas de una condición.",
          "Prefiero no responder.",
        ],
      },
      {
        key: "¿Actualmente cuentas con algún diagnóstico de neurodivergencia?",
        tipo: "multiple",
        opciones: [
          "TDAH",
          "Condición del Espectro Autista (CEA)",
          "Dislexia",
          "Discalculia",
          "Trastorno del Desarrollo del Lenguaje (TDL)",
        ],
        otro: true,
        showIf: (r: any) =>
          r[
            "¿Actualmente cuentas con algún diagnóstico por neurodivergencia?"
          ] === "Sí, cuento con uno o más diagnósticos formales.",
      },
    ],
  },
  {
    titulo: "Salud mental",
    campos: [
      {
        key: "¿Cuentas o has contado con algún diagnóstico de salud mental (vigente actualmente o en el pasado)?",
        tituloReal:
          "¿Actualmente cuentas con algún diagnóstico por salud mental?",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, cuento o he contado con uno o más diagnósticos formales.",
          "Estoy actualmente en proceso de evaluación diagnóstica.",
          "No tengo diagnóstico formal, pero sospecho que podría presentar una/s condición/es.",
          "No, no cuento ni he contado con diagnósticos ni sospechas de una condición.",
          "Prefiero no responder.",
        ],
      },
      {
        key: "¿Cuentas con alguno de los siguientes diagnósticos? (salud mental)",
        tituloReal: "¿Cuentas con alguno de los siguientes diagnósticos?",
        tipo: "multiple",
        opciones: [
          "Trastorno de Ansiedad",
          "Trastorno Depresivo",
          "Trastorno Obsesivo Compulsivo (TOC)",
          "Trastorno Bipolar",
          "No estoy segura de cuál podría ser",
        ],
        otro: true,
        showIf: (r: any) =>
          r[
            "¿Cuentas o has contado con algún diagnóstico de salud mental (vigente actualmente o en el pasado)?"
          ] === "Sí, cuento o he contado con uno o más diagnósticos formales.",
      },
    ],
  },
  {
    titulo: "Discapacidad física",
    campos: [
      {
        key: "¿Actualmente cuentas con algún diagnóstico por discapacidad física?",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, cuento con uno o más diagnósticos formales.",
          "Estoy actualmente en proceso de evaluación diagnóstica.",
          "No tengo diagnóstico formal, pero sospecho que podría presentar una/s condición/es.",
          "No, no cuento con diagnósticos ni sospechas de una condición.",
          "Prefiero no responder.",
        ],
      },
      {
        key: "¿Cuentas con alguno de los siguientes diagnósticos? (discapacidad)",
        tituloReal: "¿Cuentas con alguno de los siguientes diagnósticos?",
        tipo: "multiple",
        opciones: [
          "Discapacidad física o motora",
          "Discapacidad visual",
          "Discapacidad auditiva",
          "Discapacidad intelectual",
          "Discapacidad asociada a una condición crónica de salud",
          "Prefiero no responder",
        ],
        showIf: (r: any) =>
          r[
            "¿Actualmente cuentas con algún diagnóstico por discapacidad física?"
          ] === "Sí, cuento con uno o más diagnósticos formales.",
      },
    ],
  },
  {
    titulo: "Apoyo profesional",
    campos: [
      {
        key: "¿Actualmente recibes o has recibido apoyo profesional? (psicológico,  psiquiátrico y/o terapia ocupacional) ",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, actualmente recibo apoyo",
          "Sí, recibí apoyo anteriormente durante mi trayectoria",
          "No he recibido apoyo",
          "Prefiero no responder",
        ],
      },
    ],
  },
  {
    titulo: "Trayectoria educativa",
    campos: [
      {
        key: "Tipo de establecimiento escolar ",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Municipal/Público",
          "Particular subvencionado",
          "Particular pagado",
          "Educación en casa",
        ],
        otro: true,
      },
      {
        key: "Acceso a educación superior",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, accedí a educación superior.",
          "No, no accedí a educación superior.",
        ],
      },
      {
        key: "En el caso que sí accedió a educación superior ¿Cuál fue la principal fuente de financiamiento?",
        tipo: "unica",
        opciones: [
          "Gratuidad",
          "Beca parcial",
          "Beca completa",
          "Crédito estudiantil (CAE, Fondo Solidario u otro)",
          "Pago con recursos propios o de la familia",
          "Apoyo económico del empleador",
        ],
        showIf: (r: any) =>
          r["Acceso a educación superior"] ===
          "Sí, accedí a educación superior.",
      },
    ],
  },
  {
    titulo: "Trayectoria de vida",
    campos: [
      {
        key: "¿Tiene algún tipo de evaluación formal en Alta Capacidad?",
        tipo: "unica",
        requerido: true,
        opciones: [
          "Sí, tengo una evaluación formal realizada por algún tipo de profesional.",
          "Estoy en proceso de evaluación profesional.",
          "No",
        ],
      },
      {
        key: "¿Aprendiste a leer antes de ingresar a la educación formal? ",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No", "No lo recuerdo"],
      },
      {
        key: "¿Te aceleraron/adelantaron de curso?",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No"],
      },
      {
        key: "Durante tu etapa escolar, ¿qué tan frecuente te aburrías en clases porque sentías que los contenidos eran poco desafiantes? ",
        tipo: "escala",
        requerido: true,
        escalaLabels: ["Nada frecuente", "Muy frecuente"],
      },
      {
        key: "A lo largo de tu vida, ¿Con qué frecuencia has experimentado intereses que has explorado en profundidad? ",
        tipo: "escala",
        requerido: true,
        escalaLabels: ["Nada frecuente", "Muy frecuente"],
      },
      {
        key: "¿Con qué frecuencia has dedicado largos períodos de tiempo a aprender sobre temas específicos por iniciativa propia? ",
        tipo: "escala",
        requerido: true,
        escalaLabels: ["Nada frecuente", "Muy frecuente"],
      },
      {
        key: "¿Con qué frecuencia has sentido que aprendes con mayor rapidez que la mayoría de las personas cuando un tema despierta tu interés? ",
        tipo: "escala",
        requerido: true,
        escalaLabels: ["Nada frecuente", "Muy frecuente"],
      },
      {
        key: "¿Has recibido alguno de los siguientes reconocimientos a lo largo de tu vida? ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Premios o distinciones académicas",
          "Reconocimientos en el ámbito laboral",
          "Premios o reconocimientos artísticos/culturales",
          "Reconocimientos deportivos",
          "Becas obtenidas por mérito",
          "Selección para programas especiales de formación o talento",
          "Reconocimiento por liderazgo o participación comunitaria",
          "Ninguno de los anteriores",
        ],
      },
      {
        key: "A lo largo de tu vida, ¿has vivido alguna de las siguientes experiencias de aprendizaje? ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Aprender de manera autodidacta un tema en profundidad",
          "Completar cursos o formaciones por iniciativa propia",
          "Aprender un idioma de manera autónoma",
          "Desarrollar proyectos personales complejos",
          "Buscar información especializada por interés personal",
          "Ninguna de las anteriores",
        ],
      },
      {
        key: "¿Has desarrollado alguno de los siguientes productos, proyectos o iniciativas? ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Escribir libros, artículos, ensayos, blogs u otros textos",
          "Desarrollar software, aplicaciones o herramientas tecnológicas",
          "Diseñar o construir objetos, dispositivos o prototipos",
          "Crear obras artísticas (música, pintura, fotografía, teatro, etc.)",
          "Emprender un negocio o proyecto independiente",
          "Liderar proyectos sociales, educativos o comunitarios",
          "Participar en investigaciones científicas o académicas",
          "Desarrollar inventos o soluciones innovadoras",
          "Ninguno de los anteriores",
        ],
      },
      {
        key: "Durante alguna etapa de tu vida, ¿alguna de las siguientes personas te comentó que destacabas por tus capacidades, forma de aprender o forma de pensar? ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Familiares",
          "Docentes u otros profesionales educativos",
          "Compañeros o compañeras",
          "Jefaturas o colegas",
          "Amistades",
          "Profesionales de salud",
          "Otras personas significativas",
          "Nunca recibí ese tipo de comentarios",
        ],
      },
    ],
  },
  {
    titulo: "Acceso a recursos y oportunidades",
    nota: "Oportunidades que has tenido, dentro o fuera de espacios educativos formales, para acceder a recursos que favorecen tu aprendizaje y desarrollo.",
    campos: [
      {
        key: "Acceso a libros durante la infancia y adolescencia ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Tuve acceso a través del colegio",
          "Tuve acceso a través de mi familia",
          "Tuve acceso por otras vías",
          "No tuve acceso",
        ],
      },
      {
        key: "Acceso a tecnología digital durante la infancia y adolescencia ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Tuve acceso a través del colegio",
          "Tuve acceso a través de mi familia",
          "Tuve acceso por otras vías",
          "No tuve acceso",
        ],
      },
      {
        key: "Acceso a aprendizaje de idiomas ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Tuve acceso a través del colegio",
          "Tuve acceso a través de mi familia",
          "Tuve acceso por otras vías",
          "No tuve acceso",
        ],
      },
      {
        key: "Acceso a mentores o referentes significativos ",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Tuve acceso a través del colegio",
          "Tuve acceso a través de mi familia",
          "Tuve acceso por otras vías",
          "No tuve acceso",
        ],
      },
      {
        key: "Acceso a instancias formativas extracurriculares",
        tipo: "multiple",
        requerido: true,
        opciones: [
          "Artístico",
          "Deportivo",
          "Académico",
          "Social-comunitario",
          "Ninguno",
        ],
      },
    ],
  },
  {
    titulo: "Tamizaje",
    nota: "14 afirmaciones sobre tu forma de pensar, sentir y actuar en el día a día. No hay respuestas correctas o incorrectas.",
    campos: [
      "Tengo un vocabulario muy extenso y fascinación por las palabras",
      "Suelo tener un pensamiento profundo y abstracto de la realidad que me rodea",
      "No puedo dejar de pensar y trabajo hasta la extenuación",
      "Me cuestiono las normativas vigentes y la autoridad en general",
      "Tengo mucha energía",
      "Me percibo como distinto/a al resto de personas que conozco",
      "Percibo el mundo de forma muy distinta al resto",
      "Soy muy sincero/a",
      "Soy altamente sensible",
      "Soy independiente y casi todo lo consigo de forma autónoma",
      "Soy perfeccionista y me pongo metas altas",
      "Soy muy observador/a",
      "Busco la verdad y la justicia por encima de todo",
      "Siento mucho interés por aprender nuevas materias y habilidades",
    ].map((t) => ({ key: t, tipo: "escala", requerido: true })),
  },
  {
    titulo: "Autopercepción en Alta Capacidad",
    campos: [
      {
        key: "Pensando en tu trayectoria y en tu participación en esta investigación, ¿crees que podrías presentar características asociadas a la alta capacidad?",
        tipo: "unica",
        requerido: true,
        opciones: ["Sí", "No"],
      },
      {
        key: "¿Por qué? Justifique su respuesta anterior",
        tipo: "parrafo",
        requerido: true,
      },
    ],
  },
];

// ============================================================================
// COMPONENTES DE UI
// ============================================================================
function ProgressBar({
  step,
  total,
  tituloSeccion,
}: {
  step: number;
  total: number;
  tituloSeccion: string;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: PURPLE,
            letterSpacing: 0.3,
          }}
        >
          Sección {step + 1} de {total}
        </span>
        <span style={{ fontSize: 13, color: MUTED }}>{tituloSeccion}</span>
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: BORDER,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${((step + 1) / total) * 100}%`,
            background: `linear-gradient(90deg, ${PURPLE}, ${PURPLE_SOFT})`,
            borderRadius: 999,
            transition: "width 420ms cubic-bezier(.4,0,.2,1)",
          }}
        />
      </div>
    </div>
  );
}

function Help({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-block", marginLeft: 6 }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ayuda"
        style={{
          border: "none",
          background: "none",
          cursor: "pointer",
          padding: 0,
          verticalAlign: "middle",
          color: PURPLE_SOFT,
          display: "inline-flex",
        }}
      >
        <HelpCircle size={17} />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 26,
            zIndex: 20,
            width: 240,
            background: INK,
            color: "#fff",
            fontSize: 12.5,
            lineHeight: 1.5,
            padding: "10px 12px",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,.18)",
          }}
        >
          {text}
        </div>
      )}
    </span>
  );
}

function Label({
  children,
  requerido,
}: {
  children: any;
  requerido?: boolean;
}) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 15.5,
        fontWeight: 600,
        color: INK,
        marginBottom: 10,
      }}
    >
      {children} {requerido && <span style={{ color: ROSE }}>*</span>}
    </label>
  );
}

const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 16,
  padding: "13px 14px",
  borderRadius: 10,
  border: `1.5px solid ${BORDER}`,
  color: INK,
  background: "#fff",
  boxSizing: "border-box" as const,
  fontFamily: "inherit",
};

function BirthYearPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number) => void;
}) {
  const [decade, setDecade] = useState(
    value ? Math.floor(value / 10) * 10 : null
  );
  const currentYear = 2026;
  const decades: number[] = [];
  for (let d = 1920; d <= 2010; d += 10) decades.push(d);

  if (decade === null) {
    return (
      <div>
        <p style={{ fontSize: 13.5, color: MUTED, margin: "0 0 10px" }}>
          Primero elige tu década de nacimiento:
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {decades.map((d) => (
            <button
              key={d}
              onClick={() => setDecade(d)}
              style={{
                padding: "14px 8px",
                borderRadius: 10,
                border: `1.5px solid ${BORDER}`,
                background: "#fff",
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                cursor: "pointer",
              }}
            >
              {d}s
            </button>
          ))}
        </div>
      </div>
    );
  }

  const years: number[] = [];
  for (let y = decade; y < decade + 10 && y <= currentYear; y++) years.push(y);

  return (
    <div>
      <button
        onClick={() => setDecade(null)}
        style={{
          border: "none",
          background: "none",
          color: PURPLE_SOFT,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          padding: 0,
          marginBottom: 10,
        }}
      >
        ← Cambiar década ({decade}s)
      </button>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 8,
        }}
      >
        {years.map((y) => (
          <button
            key={y}
            onClick={() => onChange(y)}
            style={{
              padding: "12px 4px",
              borderRadius: 10,
              cursor: "pointer",
              fontSize: 14.5,
              fontWeight: 700,
              border:
                value === y ? `2px solid ${PURPLE}` : `1.5px solid ${BORDER}`,
              background: value === y ? PURPLE : "#fff",
              color: value === y ? "#fff" : INK,
            }}
          >
            {y}
          </button>
        ))}
      </div>
      {value && (
        <p
          style={{
            fontSize: 13.5,
            color: TEAL,
            fontWeight: 600,
            marginTop: 10,
          }}
        >
          <Check size={14} style={{ verticalAlign: -2, marginRight: 4 }} />{" "}
          {currentYear - value} años
        </p>
      )}
    </div>
  );
}

function OptionList({
  options,
  value,
  onChange,
  multi,
  otro,
  otroTexto,
  onOtroTexto,
}: {
  options: string[];
  value: any;
  onChange: (v: any) => void;
  multi?: boolean;
  otro?: boolean;
  otroTexto?: string;
  onOtroTexto?: (v: string) => void;
}) {
  const todas = otro ? [...options, "Otro:"] : options;
  const isSelected = (opt: string) =>
    multi ? (value || []).includes(opt) : value === opt;
  const toggle = (opt: string) => {
    if (!multi) return onChange(opt);
    const cur = value || [];
    onChange(
      cur.includes(opt) ? cur.filter((o: string) => o !== opt) : [...cur, opt]
    );
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {todas.map((opt) => (
        <div key={opt}>
          <button
            onClick={() => toggle(opt)}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "12px 16px",
              borderRadius: 10,
              cursor: "pointer",
              border: isSelected(opt)
                ? `2px solid ${PURPLE}`
                : `1.5px solid ${BORDER}`,
              background: isSelected(opt) ? "#F4EBF8" : "#fff",
              fontSize: 15,
              color: INK,
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "inherit",
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: multi ? 5 : 999,
                flexShrink: 0,
                border: `2px solid ${isSelected(opt) ? PURPLE : "#BEB3CC"}`,
                background: isSelected(opt) ? PURPLE : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected(opt) && (
                <Check size={12} color="#fff" strokeWidth={3} />
              )}
            </span>
            {opt}
          </button>
          {opt === "Otro:" && isSelected(opt) && (
            <input
              style={{ ...inputStyle, marginTop: 6, fontSize: 14 }}
              placeholder="Especifica..."
              value={otroTexto || ""}
              onChange={(e) => onOtroTexto?.(e.target.value)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ScaleField({
  value,
  onChange,
  labels,
}: {
  value: number | undefined;
  onChange: (v: number) => void;
  labels?: [string, string];
}) {
  const [izq, der] = labels || [
    "No me siento identificado/a",
    "Me siento muy identificado/a",
  ];
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11.5,
          color: MUTED,
          padding: "0 2px",
          marginBottom: 6,
        }}
      >
        <span>{izq}</span>
        <span>{der}</span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            aria-label={`Puntaje ${n} de 5`}
            style={{
              flex: 1,
              padding: "14px 0",
              borderRadius: 10,
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 700,
              border:
                value === n ? `2px solid ${PURPLE}` : `1.5px solid ${BORDER}`,
              background: value === n ? PURPLE : "#fff",
              color: value === n ? "#fff" : INK,
            }}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

// Renderiza un campo del esquema según su tipo.
function Campo({
  campo,
  valor,
  onChange,
  otroTexto,
  onOtroTexto,
  respuestas,
}: {
  campo: any;
  valor: any;
  onChange: (v: any) => void;
  otroTexto?: string;
  onOtroTexto: (v: string) => void;
  respuestas: Record<string, any>;
}) {
  switch (campo.tipo) {
    case "texto":
      return (
        <input
          style={inputStyle}
          value={valor || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "email": {
      const invalido = !!valor && !esCorreoValido(valor);
      return (
        <div>
          <input
            type="email"
            style={{
              ...inputStyle,
              border: invalido ? `1.5px solid ${PINK}` : inputStyle.border,
            }}
            value={valor || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="tu@correo.cl"
          />
          {invalido && (
            <p style={{ color: PINK, fontSize: 12.5, margin: "6px 0 0" }}>
              Ingresa un correo válido (debe contener "@").
            </p>
          )}
        </div>
      );
    }
    case "tel":
      return (
        <input
          type="tel"
          style={inputStyle}
          value={valor || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="+56 9 ..."
        />
      );
    case "parrafo":
      return (
        <textarea
          style={{ ...inputStyle, minHeight: 110, resize: "vertical" }}
          value={valor || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "anio":
      return <BirthYearPicker value={valor} onChange={onChange} />;
    case "escala":
      return (
        <ScaleField
          value={valor}
          onChange={onChange}
          labels={campo.escalaLabels}
        />
      );
    case "select": {
      // Si el campo depende de otro (comuna depende de región), las opciones
      // salen del grupo correspondiente a lo elegido en ese otro campo.
      const dependiente = !!campo.dependeDe;
      const valorPadre = dependiente ? respuestas[campo.dependeDe] : null;
      const opts: string[] = dependiente
        ? campo.opcionesPorRegion?.[valorPadre] || []
        : campo.opciones;
      const placeholder =
        dependiente && !valorPadre
          ? "Primero selecciona la región"
          : "Selecciona una opción...";
      return (
        <select
          style={{
            ...inputStyle,
            cursor: dependiente && !valorPadre ? "not-allowed" : "pointer",
            color: valor ? INK : MUTED,
          }}
          value={valor || ""}
          disabled={dependiente && !valorPadre}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {opts.map((opt: string) => (
            <option key={opt} value={opt} style={{ color: INK }}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    case "unica":
      return (
        <OptionList
          options={campo.opciones}
          value={valor}
          onChange={onChange}
          otro={campo.otro}
          otroTexto={otroTexto}
          onOtroTexto={onOtroTexto}
        />
      );
    case "multiple":
      return (
        <OptionList
          options={campo.opciones}
          value={valor}
          onChange={onChange}
          multi
          otro={campo.otro}
          otroTexto={otroTexto}
          onOtroTexto={onOtroTexto}
        />
      );
    default:
      return null;
  }
}

// ============================================================================
// CONTENIDO FIJO: descripción del proyecto (texto original del cuestionario)
// ============================================================================
const DESCRIPCION_PROYECTO = `Bienvenida/o, gracias por tu interés en participar en este estudio.

Este formulario forma parte de una investigación desarrollada por la Línea 3 del Núcleo Milenio para el Estudio Integral de la Alta Capacidad y sus Diversos Contextos [MICAI], cuyo propósito es comprender las trayectorias de desarrollo del talento y las altas capacidades en personas adultas, considerando cómo distintos factores personales, sociales, culturales y educativos influyen en su manifestación, reconocimiento y desarrollo a lo largo de la vida.

Para aquello, recopilaremos información sociodemográfica, antecedentes de trayectoria educativa y laboral, así como experiencias y características asociadas a las altas capacidades.

No es necesario contar con una evaluación formal de altas capacidades para participar.

La información proporcionada será tratada de manera confidencial y utilizada exclusivamente con fines de investigación. La participación es voluntaria y puede retirarse en cualquier momento del estudio.

El tiempo estimado para completar este formulario es de entre 10 y 15 minutos.

Es importante señalar que este formulario no es un proceso de evaluación formal, por lo que no sustituye una identificación de alta capacidad.

Agradecemos sinceramente tu interés y disposición para contribuir a la generación de conocimiento sobre las altas capacidades y las trayectorias de vida de quienes las experimentan.`;

const CONSENTIMIENTO_INFORMADO = `CONSENTIMIENTO INFORMADO

CARACTERIZACIÓN DE ADULTOS/AS ALTA CAPACIDAD Y DOBLE EXCEPCIONALIDAD

Usted ha sido invitado/a como adulto/a con alta capacidad y/o doble excepcionalidad a participar en una investigación sobre altas capacidades en el contexto chileno. Dentro de este proyecto, se le invita específicamente a participar de la investigación desarrollada en la línea 3 de este proyecto, en la que se indagará en las trayectorias y diferentes momentos vitales de personas adultas con altas capacidades.

SE LE INVITA A PARTICIPAR EN EL SIGUIENTE PROYECTO DE INVESTIGACIÓN:

1.- Título: Núcleo Milenio para el Estudio Integral de las Altas Capacidades y sus Diversos Contextos (MICAI), Línea 3.

2.- Objetivo de la investigación: Analizar los procesos de identificación, estrategias pedagógicas y el análisis interseccional de la diversidad en la población de altas capacidades en Chile, con el propósito de construir conocimiento interdisciplinario y crítico.

3.- Su participación consistirá en rellenar un formulario de caracterización sociodemográfica y de altas capacidades, lo que ocupará 30 minutos aproximadamente.

4.- Riesgos y beneficios: La metodología que se utilizará en la investigación no implica riesgos para el(la) participante. La participación no implica beneficios directos garantizados. No obstante, los resultados del estudio podrán contribuir al conocimiento científico en el área de las altas capacidades, y eventualmente a la mejora de prácticas educativas/psicosociales relacionadas con esta temática.

5.- Tipo de información que busca la investigación: el formulario tiene como finalidad recabar las características de los/as participantes para seleccionar y describir la muestra. El formulario tiene dos apartados: 1) información sociodemográfica, 2) caracterización de las altas capacidades a partir de instrumentos. A modo de ejemplo, el tipo de información que se busca apunta a responder preguntas tales como:
"Tengo un vocabulario muy extenso y fascinación por las palabras."
"Soy perfeccionista y me pongo metas altas."

6.- Participación Voluntaria: La participación en la investigación, es absolutamente voluntaria. La información recabada solo se utilizará en este estudio.

7.- Derecho a retirarse de la investigación: Igualmente, en el transcurso de la investigación y duración del proyecto, el (la) participante tendrá todo el derecho a retirarse en cualquier momento, comunicándolo al (la) investigador(a) por cualquier medio disponible, y sin que esto implique sanciones o responsabilidad para él o ella. En caso de retirarse voluntariamente en cualquier momento de la investigación, la información recopilada hasta ese momento será borrada y no será utilizada.

8.- Derecho de conocer los resultados generales de la investigación: Los resultados de este estudio, serán presentados en conferencias y artículos científicos. Si el (la) participante desea recibir los resultados de la investigación, podrá señalarlo al final de este formulario e incluir una dirección electrónica de contacto para ello.

9.- Derecho al resguardo de la identidad del (la) participante, de la información compartida y de sus datos personales.
Anonimato del (la) participante: El (la) participante no será identificado en los resultados de la investigación ni en cualquier acción que derive de ella.
Confidencialidad del (la) participante: Al participar en esta investigación, todos los datos aportados o recabados serán confidenciales y deberán mantenerse en estricta reserva por parte de las personas vinculadas al estudio, asegurado mediante un "Compromiso de Confidencialidad y no Divulgación" firmado por el equipo investigador.
Derecho a la imagen del (la) participante: En el caso que el proyecto amerite el registro visual o audiovisual de su participación en él, tendrá derecho a consentir o disentir independiente y específicamente que esto suceda.
Deber de confidencialidad: Asimismo, en mi calidad de participante de esta investigación, me comprometo a guardar la más estricta confidencialidad de las opiniones vertidas por los(as) otros(as) participantes del grupo de discusión, no pudiendo reproducir sus opiniones ni individualizar a quienes las emitieron en ninguna otra instancia.

10.- Custodio de los Datos: El(la) investigador(a) responsable guardará la información personal relacionada con el estudio por un período de 5 años posteriores al término de la investigación. Dicha información será almacenada en dispositivos digitales protegidos con contraseña. El acceso a la información será restringido a la investigadora responsable y, de corresponder, a coinvestigadores/as debidamente acreditados/as en el proyecto, quienes estarán sujetos a los mismos criterios de confidencialidad y resguardo de la información. En caso de requerirse apoyo técnico para el análisis de datos, este se realizará exclusivamente sobre información previamente anonimizada, sin acceso a antecedentes que permitan identificar a los/las participantes.

11.- Respecto de publicaciones: Se solicitará autorización al (la) participante respecto de que la información aportada aparezca en artículos o libros que se podrían publicar como resultado de esta investigación. Igualmente se solicitará su autorización para que su nombre figure en la investigación. En el caso que el(la) participante acepte aparecer con su identidad en la publicación, el(la) investigador(a) responsable, de manera previa enviará para su revisión la información a publicar. El(la) participante podrá realizar las correcciones y/u observaciones que estime pertinentes en lo que respecta a los datos por él(ella) aportados.

12.- Compensaciones: Este proyecto no contempla compensaciones retribuciones ni económicas de ninguna índole por participación del sujeto en el estudio.

13.- En caso de consultas, se puede dirigir a:
Investigadora Responsable: María Paz Gómez
Unidad académica: Psicología — Fono: (56-2) 2 718 43 52
E-mail: milenio.micai@usach.cl

14.- Identificación del Comité de Ética Institucional: En caso de reclamos, se puede dirigir al Dr. Jairo Vanegas López, Presidente del Comité de Ética de la Universidad de Santiago de Chile. Fono: (56-2) 27180294 / (56-2) 27180293. Email: comitedeetica@usach.cl

15.- Ejemplares: Este Consentimiento Informado se firma en dos ejemplares: uno para el (la) investigador(a) responsable y uno para el (la) participante.

PARTICIPANTE:
He leído este documento y he sido informado del objetivo y características de este estudio y acepto participar voluntariamente en él, en calidad de:`;

const LOCAL_STORAGE_KEY = "linea3_micai_formulario_v1";

function esCorreoValido(v: string) {
  return typeof v === "string" && v.trim().includes("@");
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function FormularioPrototipo() {
  const [step, setStep] = useState(0);
  const [respuestas, setRespuestas] = useState<Record<string, any>>({});
  const [otros, setOtros] = useState<Record<string, string>>({});
  const [participar, setParticipar] = useState<string | null>(null);
  const [enviarResultados, setEnviarResultados] = useState<string | null>(null);
  const [correoResultados, setCorreoResultados] = useState("");
  const [nombreParticipante, setNombreParticipante] = useState("");
  const [envioEstado, setEnvioEstado] = useState("idle"); // idle | enviando | ok | error
  const [errorMsg, setErrorMsg] = useState("");
  const [cargado, setCargado] = useState(false);
  const [bannerError, setBannerError] = useState(false);

  // Cargar lo guardado en este navegador, si existe (para que al recargar
  // la página no se pierda lo ya respondido).
  useEffect(() => {
    try {
      const guardado = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (guardado) {
        const datos = JSON.parse(guardado);
        if (datos.respuestas) setRespuestas(datos.respuestas);
        if (datos.otros) setOtros(datos.otros);
        if (typeof datos.participar === "string")
          setParticipar(datos.participar);
        if (typeof datos.enviarResultados === "string")
          setEnviarResultados(datos.enviarResultados);
        if (typeof datos.correoResultados === "string")
          setCorreoResultados(datos.correoResultados);
        if (typeof datos.nombreParticipante === "string")
          setNombreParticipante(datos.nombreParticipante);
        if (typeof datos.step === "number") setStep(datos.step);
      }
    } catch (err) {
      console.warn("No se pudo leer el progreso guardado:", err);
    }
    setCargado(true);
  }, []);

  // Guardar cada vez que cambia algo (una vez que ya terminamos de cargar,
  // para no sobreescribir lo guardado con el estado inicial vacío).
  useEffect(() => {
    if (!cargado) return;
    try {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({
          respuestas,
          otros,
          participar,
          enviarResultados,
          correoResultados,
          nombreParticipante,
          step,
        })
      );
    } catch (err) {
      console.warn("No se pudo guardar el progreso:", err);
    }
  }, [
    respuestas,
    otros,
    participar,
    enviarResultados,
    correoResultados,
    nombreParticipante,
    step,
    cargado,
  ]);

  // 0 = portada/consentimiento · 1..N = secciones · N+1 = envío
  const totalPasos = SECCIONES.length + 2;
  const enPortada = step === 0;
  const enEnvio = step === totalPasos - 1;
  const seccionActual = !enPortada && !enEnvio ? SECCIONES[step - 1] : null;

  const setValor = (key: string, val: any) =>
    setRespuestas((r) => {
      const next = { ...r, [key]: val };
      // Al cambiar la región, se limpia la comuna elegida (puede no pertenecer
      // a la nueva región).
      if (key === "Región de residencia") next["Comuna de residencia"] = "";
      return next;
    });
  const setOtro = (key: string, val: string) =>
    setOtros((o) => ({ ...o, [key]: val }));

  // La sección actual tiene algún campo de correo con contenido pero sin "@"?
  const correoInvalidoEnSeccion = seccionActual
    ? seccionActual.campos.some(
        (c) =>
          c.tipo === "email" &&
          respuestas[c.key] &&
          !esCorreoValido(respuestas[c.key])
      )
    : false;

  // ¿El campo tiene una respuesta válida? (todas las preguntas son obligatorias)
  const campoRespondido = (campo: CampoDef): boolean => {
    const v = respuestas[campo.key];
    const otroTexto = (otros[campo.key] || "").trim();
    switch (campo.tipo) {
      case "multiple":
        if (!Array.isArray(v) || v.length === 0) return false;
        if (v.includes("Otro:") && !otroTexto) return false;
        return true;
      case "email":
        return typeof v === "string" && esCorreoValido(v);
      case "escala":
        return typeof v === "number";
      case "anio":
        return v !== undefined && v !== null && v !== "";
      case "unica":
        if (v === undefined || v === null || v === "") return false;
        if (v === "Otro:" && !otroTexto) return false;
        return true;
      default: // texto, tel, parrafo, select
        return typeof v === "string" ? v.trim() !== "" : !!v;
    }
  };

  // Una sección solo se completa si TODOS sus campos visibles están respondidos.
  const seccionCompleta = seccionActual
    ? seccionActual.campos
        .filter((c) => !c.showIf || c.showIf(respuestas))
        .every((c) => campoRespondido(c))
    : true;

  const puedeAvanzar = enPortada
    ? participar === "Sí" &&
      nombreParticipante.trim() !== "" &&
      esCorreoValido(correoResultados) &&
      enviarResultados !== null
    : seccionCompleta && !correoInvalidoEnSeccion;

  const next = () => setStep((s) => Math.min(s + 1, totalPasos - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  // Al cambiar de página, volver arriba (no quedar al fondo del scroll).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  const enviar = async () => {
    console.log(
      "🟣 enviar() se ejecutó. ENDPOINT_URL =",
      ENDPOINT_URL || "(vacío)"
    );
    setEnvioEstado("enviando");
    setErrorMsg("");

    // Arma el payload final: título real de la pregunta -> valor. Si el
    // valor elegido fue "Otro:", se reemplaza por el texto libre que
    // escribió la persona (si lo hay).
    const payload: Record<string, any> = {};
    SECCIONES.forEach((sec) => {
      sec.campos.forEach((campo) => {
        if (campo.showIf && !campo.showIf(respuestas)) return; // no visible -> no se envía
        let val = respuestas[campo.key];
        if (val === undefined || val === null) return;
        const tituloReal = campo.tituloReal || campo.key;
        if (Array.isArray(val)) {
          val = val.map((v) =>
            v === "Otro:" ? otros[campo.key] || "Otro" : v
          );
        } else if (val === "Otro:") {
          val = otros[campo.key] || "Otro";
        }
        payload[tituloReal] = val;
      });
    });

    // Nombre, correo y consentimiento viven fuera de SECCIONES (se piden en
    // la portada una sola vez).
    if (nombreParticipante.trim())
      payload["Nombre completo"] = nombreParticipante.trim();
    if (correoResultados.trim())
      payload["Correo de contacto"] = correoResultados.trim();
    if (participar) payload["Consentimiento Informado"] = participar;

    console.log("🟣 Payload a enviar:", payload);

    if (!ENDPOINT_URL) {
      await new Promise((r) => setTimeout(r, 900));
      console.log(
        "Simulación de envío (configura ENDPOINT_URL para enviar de verdad):",
        payload
      );
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setEnvioEstado("ok");
      return;
    }

    try {
      console.log("🟣 Llamando a fetch()...");
      const r = await fetch(ENDPOINT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const res = await r.json();
      console.log("🟣 Respuesta del servidor:", res);
      if (res.ok) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setEnvioEstado("ok");
      } else {
        setEnvioEstado("error");
        setErrorMsg(res.error || "No se pudo guardar.");
      }
    } catch (err) {
      console.error("🔴 fetch() lanzó un error:", err);
      setEnvioEstado("error");
      setErrorMsg(String(err));
    }
  };

  return (
    <div
      style={{
        background: BG,
        minHeight: "100%",
        fontFamily: "'Inter', system-ui, sans-serif",
        padding: "28px 16px 100px",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Montserrat:wght@600;700;800&display=swap');
        h1, h2, h3, .fuente-secundaria { font-family: 'Montserrat', 'Inter', system-ui, sans-serif; }
        * { box-sizing: border-box; }
        button:focus-visible, input:focus-visible, textarea:focus-visible { outline: 3px solid ${TEAL}; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        {/*
          IMPORTANTE: esta imagen NO viaja dentro de este archivo de código.
          Para que cargue, el archivo "banner_micai.png" debe estar guardado
          físicamente en la carpeta "public/" de tu proyecto (al mismo nivel
          que "public/index.html"), con ese nombre exacto. Si el banner no
          aparece, revisa primero eso — es la causa más común. El bloque de
          abajo, además, avisa por consola y muestra un aviso visible si el
          archivo no se encuentra, para que no pase inadvertido.
        */}
        <img
          src="/banner_micai.png"
          alt="MICAI - Núcleo Milenio para el Estudio Integral de la Alta Capacidad y sus Diversos Contextos"
          style={{
            width: "100%",
            height: "auto",
            borderRadius: 12,
            marginBottom: 18,
            display: bannerError ? "none" : "block",
          }}
          onError={() => {
            console.error(
              "🔴 No se pudo cargar /banner_micai.png. Verifica que el archivo esté en la carpeta 'public/' de tu proyecto, con ese nombre exacto."
            );
            setBannerError(true);
          }}
        />
        {bannerError && (
          <div
            style={{
              background: "#FDF3E7",
              border: "1px solid #E8C68A",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: 12.5,
              color: "#7A5A1E",
              marginBottom: 18,
              lineHeight: 1.5,
            }}
          >
            No se encontró <code>banner_micai.png</code>. Cópialo dentro de la
            carpeta <code>public/</code> de tu proyecto (junto a{" "}
            <code>index.html</code>) y recarga la página.
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: PURPLE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            L3
          </div>
          <div>
            <p
              style={{
                margin: 0,
                fontWeight: 800,
                fontSize: 15,
                color: PURPLE,
              }}
              className="fuente-secundaria"
            >
              Línea 3 · MICAI
            </p>
            <p style={{ margin: 0, fontSize: 12.5, color: MUTED }}>
              Caracterización de participantes
            </p>
          </div>
        </div>

        <ProgressBar
          step={step}
          total={totalPasos}
          tituloSeccion={
            enPortada
              ? "Bienvenida y consentimiento"
              : seccionActual
              ? seccionActual.titulo
              : "Enviar"
          }
        />

        <div
          style={{
            background: CARD,
            borderRadius: 18,
            padding: "26px 24px",
            boxShadow:
              "0 1px 3px rgba(60,0,80,0.06), 0 12px 32px rgba(60,0,80,0.06)",
          }}
        >
          {enPortada ? (
            <div>
              <h2 style={{ fontSize: 21, margin: "0 0 14px", color: INK }}>
                Antes de comenzar
              </h2>
              <div
                style={{
                  fontSize: 13.5,
                  color: INK,
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                  marginBottom: 20,
                }}
              >
                {DESCRIPCION_PROYECTO}
              </div>

              <Label>Consentimiento informado</Label>
              <div
                style={{
                  maxHeight: 260,
                  overflowY: "auto",
                  border: `1.5px solid ${BORDER}`,
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 20,
                  background: BG,
                  fontSize: 12.5,
                  color: INK,
                  lineHeight: 1.6,
                  whiteSpace: "pre-line",
                }}
              >
                {CONSENTIMIENTO_INFORMADO}
              </div>

              <p
                style={{
                  fontSize: 13,
                  color: INK,
                  lineHeight: 1.5,
                  margin: "-8px 0 20px",
                }}
              >
                Puedes revisar o descargar el{" "}
                <a
                  href="https://drive.google.com/file/d/1AsHo-TtjQFswec59sXkNRn1qhwX7gRDq/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: PURPLE, fontWeight: 600 }}
                >
                  documento de consentimiento informado completo
                </a>
                .
              </p>
              <div style={{ marginBottom: 22 }}>
                <Label requerido>
                  Deseo participar del estudio mediante el protocolo de
                  caracterización
                </Label>
                <OptionList
                  options={["Sí", "No"]}
                  value={participar}
                  onChange={setParticipar}
                />
                {participar === "No" && (
                  <p
                    style={{
                      fontSize: 12.5,
                      color: MUTED,
                      marginTop: 10,
                      lineHeight: 1.5,
                    }}
                  >
                    Entendemos tu decisión. Gracias por tu tiempo — puedes
                    cerrar esta página cuando quieras.
                  </p>
                )}
              </div>

              {participar === "Sí" && (
                <>
                  <div style={{ marginBottom: 22 }}>
                    <Label requerido>Nombre completo</Label>
                    <input
                      style={inputStyle}
                      value={nombreParticipante}
                      onChange={(e) => setNombreParticipante(e.target.value)}
                      placeholder="Tu nombre completo"
                    />
                  </div>

                  <div style={{ marginBottom: 22 }}>
                    <Label requerido>Correo de contacto</Label>
                    <input
                      type="email"
                      style={{
                        ...inputStyle,
                        border:
                          correoResultados && !esCorreoValido(correoResultados)
                            ? `1.5px solid ${PINK}`
                            : inputStyle.border,
                      }}
                      value={correoResultados}
                      onChange={(e) => setCorreoResultados(e.target.value)}
                      placeholder="tu@correo.cl"
                    />
                    {correoResultados && !esCorreoValido(correoResultados) && (
                      <p
                        style={{
                          color: PINK,
                          fontSize: 12.5,
                          margin: "6px 0 0",
                        }}
                      >
                        Ingresa un correo válido (debe contener "@").
                      </p>
                    )}
                  </div>

                  <div style={{ marginBottom: 4 }}>
                    <Label requerido>
                      Deseo que los(as) investigadores(as) me envíen los
                      resultados generales del estudio a este correo
                    </Label>
                    <OptionList
                      options={["Sí", "No"]}
                      value={enviarResultados}
                      onChange={setEnviarResultados}
                    />
                  </div>
                </>
              )}
            </div>
          ) : seccionActual ? (
            <div>
              <h2 style={{ fontSize: 20, margin: "0 0 6px", color: INK }}>
                {seccionActual.titulo}
              </h2>
              {seccionActual.nota && (
                <p
                  style={{
                    fontSize: 13.5,
                    color: MUTED,
                    lineHeight: 1.5,
                    margin: "0 0 18px",
                    whiteSpace: "pre-line",
                  }}
                >
                  {seccionActual.nota}
                </p>
              )}
              {seccionActual.campos
                .filter((campo) => !campo.showIf || campo.showIf(respuestas))
                .map((campo) => (
                  <div key={campo.key} style={{ marginBottom: 24 }}>
                    <Label requerido>{campo.key.trim()}</Label>
                    <Campo
                      campo={campo}
                      valor={respuestas[campo.key]}
                      onChange={(v) => setValor(campo.key, v)}
                      otroTexto={otros[campo.key]}
                      onOtroTexto={(v) => setOtro(campo.key, v)}
                      respuestas={respuestas}
                    />
                  </div>
                ))}
            </div>
          ) : (
            <div>
              {envioEstado !== "ok" && (
                <>
                  <h2 style={{ fontSize: 21, margin: "0 0 10px", color: INK }}>
                    Revisa y envía
                  </h2>
                  <p
                    style={{
                      fontSize: 14,
                      color: MUTED,
                      lineHeight: 1.5,
                      margin: "0 0 20px",
                    }}
                  >
                    Ya completaste las {SECCIONES.length} secciones. Al
                    presionar el botón, tus respuestas se envían al formulario
                    de Línea 3.
                  </p>

                  {envioEstado === "error" && (
                    <div
                      style={{
                        background: "#FDEBEE",
                        border: "1px solid #E8A0AC",
                        borderRadius: 10,
                        padding: "12px 14px",
                        fontSize: 13,
                        color: "#8C2E3B",
                        marginBottom: 18,
                      }}
                    >
                      No se pudo enviar: {errorMsg}
                    </div>
                  )}

                  <button
                    onClick={enviar}
                    disabled={envioEstado === "enviando"}
                    style={{
                      width: "100%",
                      padding: "15px 0",
                      borderRadius: 10,
                      border: "none",
                      background: PURPLE,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 15.5,
                      cursor:
                        envioEstado === "enviando" ? "default" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    {envioEstado === "enviando" ? (
                      <>
                        <Loader2
                          size={18}
                          style={{ animation: "spin 900ms linear infinite" }}
                        />{" "}
                        Enviando...
                      </>
                    ) : (
                      "Enviar mis respuestas"
                    )}
                  </button>
                </>
              )}

              {envioEstado === "ok" && (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 999,
                      background: TEAL,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                    }}
                  >
                    <Check size={28} color="#fff" strokeWidth={3} />
                  </div>
                  <h2 style={{ fontSize: 20, margin: "0 0 8px", color: INK }}>
                    ¡Gracias por participar!
                  </h2>
                  <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.5 }}>
                    Tus respuestas fueron registradas. El equipo de Línea 3 las
                    tratará de forma confidencial.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <p
          style={{
            textAlign: "center",
            fontSize: 12.5,
            color: MUTED,
            marginTop: 14,
          }}
        >
          Puedes guardar tus respuestas y continuar más tarde en cualquier
          momento.
        </p>
      </div>

      {!(enEnvio && envioEstado === "ok") && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            background: "#fff",
            borderTop: `1px solid ${BORDER}`,
            padding: "14px 16px",
            display: "flex",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <div
            style={{ width: "100%", maxWidth: 560, display: "flex", gap: 12 }}
          >
            <button
              onClick={back}
              disabled={step === 0}
              style={{
                flex: 1,
                padding: "14px 0",
                borderRadius: 10,
                border: `1.5px solid ${BORDER}`,
                background: "#fff",
                color: step === 0 ? "#C9C1D6" : INK,
                fontWeight: 700,
                fontSize: 15,
                cursor: step === 0 ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <ChevronLeft size={18} /> Anterior
            </button>
            {!enEnvio && (
              <button
                onClick={next}
                disabled={!puedeAvanzar}
                style={{
                  flex: 1.4,
                  padding: "14px 0",
                  borderRadius: 10,
                  border: "none",
                  background: puedeAvanzar ? PURPLE : "#C9C1D6",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: puedeAvanzar ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                Siguiente <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
